import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, MapPin, Users, Zap } from "lucide-react";
import type { CanvassingTerritory, CanvassingPin } from "@/hooks/useCanvassing";
import { cn } from "@/lib/utils";

interface CanvassingTerritoriesProps {
  territories: CanvassingTerritory[];
  pins: CanvassingPin[];
  onCreateTerritory: (t: {
    name: string;
    description?: string;
    center_lat: number;
    center_lng: number;
    radius_miles?: number;
    storm_triggered?: boolean;
    storm_date?: string;
  }) => Promise<any>;
}

export function CanvassingTerritories({ territories, pins, onCreateTerritory }: CanvassingTerritoriesProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState("1");

  const handleCreate = async () => {
    if (!name || !lat || !lng) return;
    await onCreateTerritory({
      name,
      description: description || undefined,
      center_lat: parseFloat(lat),
      center_lng: parseFloat(lng),
      radius_miles: parseFloat(radius) || 1,
    });
    setName("");
    setDescription("");
    setLat("");
    setLng("");
    setRadius("1");
    setOpen(false);
  };

  const useCurrentLocation = () => {
    navigator.geolocation?.getCurrentPosition((pos) => {
      setLat(pos.coords.latitude.toString());
      setLng(pos.coords.longitude.toString());
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Territories</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <Plus className="w-4 h-4" /> New Territory
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Territory</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Territory name" value={name} onChange={e => setName(e.target.value)} />
              <Input placeholder="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Center latitude" value={lat} onChange={e => setLat(e.target.value)} type="number" step="any" />
                <Input placeholder="Center longitude" value={lng} onChange={e => setLng(e.target.value)} type="number" step="any" />
              </div>
              <Button variant="outline" size="sm" onClick={useCurrentLocation} className="gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Use My Location
              </Button>
              <Input placeholder="Radius (miles)" value={radius} onChange={e => setRadius(e.target.value)} type="number" step="0.1" />
              <Button onClick={handleCreate} className="w-full" disabled={!name || !lat || !lng}>
                Create Territory
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {territories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-muted-foreground">No territories yet</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Create territories to organize your canvassing efforts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {territories.map(t => {
            const territoryPins = pins.filter(p => p.territory_id === t.id);
            const appts = territoryPins.filter(p => p.outcome === "appointment_set" || p.outcome === "sold").length;
            return (
              <Card key={t.id} className="hover:border-primary/30 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">{t.name}</CardTitle>
                    <div className="flex gap-1.5">
                      {t.storm_triggered && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          <Zap className="w-3 h-3" /> Storm
                        </Badge>
                      )}
                      <Badge variant={t.status === "active" ? "default" : "secondary"} className="text-xs">
                        {t.status}
                      </Badge>
                    </div>
                  </div>
                  {t.description && (
                    <p className="text-xs text-muted-foreground">{t.description}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-foreground">{territoryPins.length}</p>
                      <p className="text-xs text-muted-foreground">Doors</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">{appts}</p>
                      <p className="text-xs text-muted-foreground">Appts</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-primary">
                        {territoryPins.length > 0 ? Math.round((appts / territoryPins.length) * 100) : 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">Conv.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
